import React, { useState } from 'react';
import { Play, ExternalLink, Clock, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';

// Episodes data - Update these when new episodes are posted
const episodes = [
  {
    id: 1,
    videoId: "EBIHQIrNwwk",
    title: "Exit Big Pharma: Dr. David Duizer Reveals Naturopathic Freedom",
    description: "Discover how to reclaim your health autonomy with naturopathic medicine. Dr. David Duizer shares insights on breaking free from pharmaceutical dependence.",
    badge: null,
    badgeColor: null
  },
  {
    id: 2,
    videoId: "_rEmQqBMcx4",
    title: "Food Security Isn't Enough. Here's What Most Families Miss",
    description: "Beyond stockpiling: Learn what true food sovereignty looks like and the critical steps most families overlook in their preparedness journey.",
    badge: null,
    badgeColor: null
  },
  {
    id: 3,
    videoId: "H7xo5B8Omgg",
    title: "From Venezuela to Financial Freedom: Mauricio Di Bartolomeo on Bitcoin",
    description: "A powerful story of escaping hyperinflation and building Ledn. Mauricio shares lessons on Bitcoin, resilience, and financial sovereignty.",
    badge: null,
    badgeColor: null
  },
  {
    id: 4,
    videoId: "BiJ3ovDM6Vk",
    title: "The Sovereign Child: Raising Truly Autonomous Humans",
    description: "Rewriting the rules of freedom and what it means to raise children who think for themselves. Sovereign education for the next generation.",
    badge: null,
    badgeColor: null
  }
];

const EpisodeCard = ({ episode, isDark }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const thumbnailUrl = `https://img.youtube.com/vi/${episode.videoId}/hqdefault.jpg`;
  const youtubeUrl = `https://www.youtube.com/watch?v=${episode.videoId}`;

  return (
    <a
      href={youtubeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`group block rounded-2xl overflow-hidden border transition-all duration-300 ${
        isDark 
          ? 'bg-stone-900/50 border-stone-800 hover:border-amber-500/50 hover:shadow-xl hover:shadow-amber-500/10' 
          : 'bg-white border-stone-200 hover:border-amber-500/50 hover:shadow-xl'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={thumbnailUrl}
          alt={episode.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Play overlay */}
        <div className={`absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center shadow-lg transform transition-transform duration-300 group-hover:scale-110">
            <Play className="w-7 h-7 text-stone-950 ml-1" fill="currentColor" />
          </div>
        </div>
        
        {/* Badge */}
        {episode.badge && (
          <div className="absolute top-3 left-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              episode.badgeColor === 'amber' 
                ? 'bg-amber-500 text-stone-950' 
                : 'bg-green-500 text-white'
            }`}>
              {episode.badge}
            </span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5">
        <h3 className={`font-bold text-lg mb-2 line-clamp-2 group-hover:text-amber-500 transition-colors ${
          isDark ? 'text-stone-100' : 'text-stone-800'
        }`}>
          {episode.title}
        </h3>
        <p className={`text-sm line-clamp-2 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
          {episode.description}
        </p>
      </div>
    </a>
  );
};

const EpisodesSection = () => {
  const { isDark } = useTheme();

  return (
    <section id="episodes" className={`py-24 ${isDark ? 'bg-stone-950' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-amber-500" />
              <span className="text-amber-500 font-medium uppercase tracking-wider text-sm">Featured Episodes</span>
            </div>
            <h2 className={`text-4xl sm:text-5xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
              Start Listening
            </h2>
          </div>
          
          <Button
            variant="outline"
            className={`group self-start sm:self-auto ${
              isDark 
                ? 'border-stone-700 text-stone-300 hover:bg-stone-800 hover:border-amber-500/50' 
                : 'border-stone-300 text-stone-600 hover:bg-stone-50 hover:border-amber-500/50'
            }`}
            onClick={() => window.open('https://www.youtube.com/@UnshackledLiving', '_blank')}
          >
            View All Episodes
            <ExternalLink className="w-4 h-4 ml-2 group-hover:text-amber-500 transition-colors" />
          </Button>
        </div>

        {/* Episodes Grid - 4 cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {episodes.map((episode) => (
            <EpisodeCard key={episode.id} episode={episode} isDark={isDark} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
            New episodes every week • Available on{' '}
            <a href="https://www.youtube.com/@UnshackledLiving" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">YouTube</a>,{' '}
            <a href="https://open.spotify.com/show/3d1EbfZcId0JSMXhsl0yGi" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">Spotify</a>, and{' '}
            <a href="https://podcasts.apple.com/us/podcast/unshackled-living/id1611582704" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">Apple Podcasts</a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default EpisodesSection;
